import React, {
  RefObject,
  useContext,
  useMemo,
} from 'react';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  EditableRendererProps,
  VirtualTableColumnDef as ColumnDef,
} from 'core/components/VirtualTable/types';
import type {
  ClaimServiceLineFormDTO,
} from 'dtos';
import {
  getClaimAuthorizationOptionsForCode,
} from 'views/EMR/components/Authorizations/utils';
import AuthorizationSelector, {
  AuthorizationValue,
} from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector';
import {
  useAuthorizationCreation,
} from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationCreationContext';
import {
  ServiceContext,
} from 'views/EMR/components/ServiceLookup/ServiceContext';
import {
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';

type AuthorizationEditorProps = Omit<
  EditableRendererProps<ClaimServiceLineFormDTO | undefined>,
  'data'
> & {
  data?: VisitNoteServiceCode;
};

type AuthorizationIdleCellProps = {
  data: VisitNoteServiceCode;
};

function AuthorizationEditor({
  value,
  data,
  controlRef,
  onChange,
}: AuthorizationEditorProps) {
  const { claimAuthorizationOptions = [] } = useContext(ServiceContext);
  const { openAuthorizationCreation } = useAuthorizationCreation();
  const instanceId = data?.__id;
  const options = useMemo(
    () => getClaimAuthorizationOptionsForCode(
      claimAuthorizationOptions,
      value?.authorizationNumber,
      data?.code,
    ),
    [claimAuthorizationOptions, value?.authorizationNumber, data?.code],
  );

  return (
    <AuthorizationSelector
      id={`claim-line-authorization-${instanceId ?? data?.code ?? ''}`}
      value={{
        authorizationNumber: value?.authorizationNumber,
      }}
      options={options}
      inputRef={controlRef as RefObject<HTMLInputElement>}
      compact
      showDropdownIcon={false}
      onChange={(selection) => onChange?.({
        ...value,
        authorizationNumber: selection.authorizationNumber ?? null,
      })}
      onAddAuthorization={(authorizationNumber) => {
        if (notEmptyString(instanceId)) {
          openAuthorizationCreation(authorizationNumber, {
            type: 'serviceLine',
            instanceId: instanceId ?? '',
          });
        }
      }}
    />
  );
}

function AuthorizationIdleCell({ data }: AuthorizationIdleCellProps) {
  const { claimAuthorizationOptions = [] } = useContext(ServiceContext);
  const options = useMemo(
    () => getClaimAuthorizationOptionsForCode(
      claimAuthorizationOptions,
      data.claimServiceLine?.authorizationNumber,
      data.code,
    ),
    [
      claimAuthorizationOptions,
      data.claimServiceLine?.authorizationNumber,
      data.code,
    ],
  );

  return (
    <AuthorizationValue
      selection={{
        authorizationNumber: data.claimServiceLine?.authorizationNumber,
      }}
      options={options}
    />
  );
}

const editableMeta = {
  editable: {
    Component: AuthorizationEditor,
    alwaysEditable: true,
    mode: 'onChange' as const,
  },
};

export default {
  id: 'patientAuthorization',
  accessorFn: (row: VisitNoteServiceCode) => row.claimServiceLine,
  cell: ({ row }) => <AuthorizationIdleCell data={row.original} />,
  meta: editableMeta,
  maxSize: 240,
  minSize: 150,
  size: 190,
} as ColumnDef<VisitNoteServiceCode>;
